"use client";

import { ArrowLeft, ArrowRight, Calendar, Tag, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="h-12 w-3/4 animate-pulse rounded-md bg-muted/60" />
        <div className="h-6 w-1/2 animate-pulse rounded-md bg-muted/40" />
        <div className="h-96 w-full animate-pulse rounded-lg bg-muted/40" />
        <div className="space-y-3">
          <div className="h-4 w-full animate-pulse rounded bg-muted/40" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/40" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted/40" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl text-center">
        <p className="font-default text-lg text-muted-foreground">
          Unable to load this intel at the moment.
        </p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-2xl text-center">
        <p className="font-default text-lg text-muted-foreground">
          No transmission found.
        </p>
      </div>
    );
  }

  const post = posts[0];
  const categories = post.categories || [];
  const tags = post.tags || [];
  const author = post.author;

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="mx-auto max-w-5xl">
      {}
      <div className="mb-10">
        <Link
          to="/TheGridPage"
          className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-primary transition-colors font-default"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to The Grid
        </Link>
      </div>

      {}
      <header className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12 mb-12 md:mb-16">
        <div className="md:col-span-8 md:col-start-1">
          {categories.length > 0 && (
            <div className="mb-6 flex flex-wrap gap-2">
              {categories.map((cat, i) => (
                <span
                  data-index={i}
                  key={cat.id}
                  className="inline-flex items-center rounded-lg bg-accent px-3 py-1 text-xs uppercase tracking-widest text-accent-foreground font-mono"
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}

          <h1
            className="font-default text-4xl leading-tight text-foreground md:text-6xl lg:text-7xl"
            dangerouslySetInnerHTML={{ __html: post.title }}
          />

          {post.excerpt && (
            <div
              className="mt-6 font-default text-xl text-foreground/75 md:text-2xl leading-relaxed italic"
              dangerouslySetInnerHTML={{ __html: post.excerpt }}
            />
          )}
        </div>

        <div className="md:col-span-3 md:col-start-10 md:pt-4">
          <div className="space-y-6 border-l border-border pl-6 md:pl-8">
            {author && (
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 rounded-lg">
                  {author.avatar?.["96"] && (
                    <AvatarImage src={author.avatar["96"]} alt={author.name} />
                  )}
                  <AvatarFallback className="rounded-lg bg-secondary text-secondary-foreground text-xs">
                    {author.name?.charAt(0) || "P"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                    Operator
                  </p>
                  <p className="font-default text-base text-foreground">
                    {author.name}
                  </p>
                </div>
              </div>
            )}

            {formattedDate && (
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1">
                  Transmission
                </p>
                <p className="font-default text-base text-foreground flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {formattedDate}
                </p>
              </div>
            )}
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 md:mb-20 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-11 md:col-start-2">
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img
                src={post.featuredImage}
                alt={post.title}
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      )}

      {}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12 mb-16">
        <div className="md:col-span-8 md:col-start-3">
          {post.content && (
            <div
              className="font-default text-lg md:text-xl leading-relaxed text-foreground/90 prose prose-lg max-w-none [&_p]:mb-6 [&_h2]:font-default [&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-primary [&_h3]:font-default [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-primary [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:my-8 [&_img]:rounded-lg [&_img]:my-8 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          )}

          {}
          {tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex items-start gap-3 flex-wrap">
                <Tag className="h-4 w-4 mt-1 text-primary" />
                {tags.map((tag, i) => (
                  <span
                    data-index={i}
                    key={tag.id}
                    className="text-sm text-foreground/70 hover:text-primary transition-colors font-mono"
                  >
                    #{tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Separator className="my-12 md:my-16" />

      {}
      {author && (
        <div className="mb-16 md:mb-20 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-10 md:col-start-2">
            <Card className="bg-card border border-border shadow-md">
              <CardContent className="flex flex-col md:flex-row items-start gap-6 p-8 md:p-10">
                <Avatar className="h-20 w-20 rounded-lg shrink-0">
                  {author.avatar?.["96"] && (
                    <AvatarImage src={author.avatar["96"]} alt={author.name} />
                  )}
                  <AvatarFallback className="rounded-lg bg-secondary text-secondary-foreground text-2xl font-default">
                    {author.name?.charAt(0) || "P"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-primary font-mono mb-2 flex items-center gap-2">
                    <User className="h-3 w-3" />
                    Pro Player Profile
                  </p>
                  <h3 className="font-default text-2xl md:text-3xl text-foreground mb-3">
                    {author.name}
                  </h3>
                  <p className="font-default text-base text-foreground/70 leading-relaxed">
                    Dominating the digital arena with precision and strategy. Pixel Force elite member dedicated to competitive excellence and community growth.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsList() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-4">
            <div className="h-56 w-full animate-pulse rounded-lg bg-muted/40" />
            <div className="h-6 w-3/4 animate-pulse rounded bg-muted/40" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-muted/30" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Link
          data-index={i}
          key={post.id}
          to={post.link || "#"}
          className="group block"
        >
          <Card className="bg-card border border-border shadow-md overflow-hidden h-full hover:shadow-lg transition-shadow">
            {post.featuredImage && (
              <div className="overflow-hidden aspect-[4/3]">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            )}
            <CardContent className="p-6">
              {post.categories && post.categories.length > 0 && (
                <p className="text-xs uppercase tracking-widest text-primary font-mono mb-3">
                  {post.categories[0].name}
                </p>
              )}
              <h3
                className="font-default text-2xl text-foreground leading-tight mb-3 group-hover:text-primary transition-colors"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />
              {post.excerpt && (
                <div
                  className="font-default text-base text-foreground/70 line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              )}
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-primary font-default">
                View Intel
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
  });

  return (
    <section data-section-id="3209"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 md:py-24 lg:py-32"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-16 md:mt-24">
          <div className="mb-12 md:mb-16 grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <p className="text-xs uppercase tracking-widest text-primary font-mono mb-4">
                Next Missions
              </p>
              <h2 className="font-default text-4xl md:text-5xl text-foreground leading-tight">
                More from the Arena
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
                <RelatedPostsList />
              </PostsProvider>

              <div className="mt-12 flex justify-center">
                <Link to="/TheGridPage">
                  <Button
                    variant="outline"
                    size="lg"
                    className="rounded-lg font-default text-base px-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    View all intel
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}